import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, CircularProgress } from '@material-ui/core';
import { GenericMessageBox } from '@moderepo/mode-app-components';
import { RobotDefinition } from '@moderepo/mode-apis';
import clsx from 'clsx';
import { fetchRobotCatalog, rcDataContext, selectRobotCatalog } from '@moderepo/mode-data-state-robot-cloud';
import { modeUIContext, showSlideOutPanel, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import { RCRobotDefinitionsTable } from '../../../components/robot-cloud';
import { RCRobotDefinitionsTableDataItem } from '../../../components/robot-cloud/admin/RCRobotDefinitionsTable';
import { RCRobotDefinitionPreviewCompOptions, RCSlideOutPanelCompType } from '../RCProjectConfig';
import { RCRouteHelper } from '../RCRouteHelper';



export const RCRobotCatalogPage: React.FC = () => {
    const params = useParams();
    const projectId = Number(params.projectId);
    const navigate = useNavigate();

    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const robotCatalog = selectRobotCatalog(rcDataState, projectId);
    const [selectedRobotClass, setSelectedRobotClass] = useState < string | undefined>();
    const routeHelper = RCRouteHelper.getInstance(navigate);


    useEffect(() => {
        if (!robotCatalog) {
            rcDataDispatch(fetchRobotCatalog(projectId)).catch(() => {
                // ignore error
            });
        }
    }, [robotCatalog, projectId, rcDataDispatch]);


    const onRobotDefinitionSelected = useCallback((robotClass: string | undefined) => {
        setSelectedRobotClass(robotClass);

        const options: RCRobotDefinitionPreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : RCSlideOutPanelCompType.ROBOT_DEFINITION_PREVIEW,
            compProps: {
                data: { projectId, robotClass },
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [projectId, uiDispatch]);



    const localizeRobotCatalogData = useMemo((): readonly RCRobotDefinitionsTableDataItem [] | undefined => {
        if (robotCatalog) {
            return robotCatalog.map((robotDef: RobotDefinition): RCRobotDefinitionsTableDataItem => {
                return {
                    displayValue: {
                        name             : robotDef.name || '',
                        robotClass       : robotDef.robotClass,
                        communicationType: robotDef.modeCommunicationType,
                    },
                    actualValue: robotDef,
                    selected   : selectedRobotClass === robotDef.robotClass,
                };
            });
        }

        return undefined;
    }, [robotCatalog, selectedRobotClass]);


    return (
        <div className={clsx('page-main-content')}>
            <div className="title-bar">
                <div className="title-bar-item title-text">
                    <div className="page-title">
                        Robot Catalog
                    </div>
                    <div className="page-subtitle">
                        Edit Robot Definition
                    </div>
                </div>
                <div>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => {
                            routeHelper.gotoGatewayCatalogPage({ projectId });
                        }}
                    >
                        Gateway Catalog
                    </Button>
                </div>
            </div>

            {robotCatalog && (
                <RCRobotDefinitionsTable
                    listData={localizeRobotCatalogData}
                    title="Robots Definition"
                    icon="router"
                    customPrimaryActions={[{
                        label  : 'Add',
                        variant: 'contained',
                        color  : 'primary',
                        onClick: () => {
                            onRobotDefinitionSelected(undefined);
                        },
                    }]}
                    onListItemSelected={(robotDef: RobotDefinition) => {
                        onRobotDefinitionSelected(robotDef.robotClass);
                    }}
                    fieldsSettings={{
                        fields: {
                            robotClass: {
                                dataItemProp: 'robotClass',
                                label       : 'Robot Class',
                            },
                            name: {
                                dataItemProp: 'name',
                                label       : 'Name',
                            },
                            communicationType: {
                                dataItemProp: 'communicationType',
                                label       : 'Comm. Type',
                            },
                        },
                    }}
                    noDataMessageComp={(
                        <GenericMessageBox
                            title="No Robot Definition"
                        />
                    )}
                    emptyDataMessageComp={(
                        <GenericMessageBox
                            title="No Robot Definition"
                        />
                    )}
                />
            )}

            {!robotCatalog && (
                <CircularProgress />
            )}
        </div>
    );
};
